import { useEffect, useState } from 'react';
import { shortDescriptionActions } from 'tradera/apps/syi/state/actions/short-description-actions';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import { useTranslator } from 'tradera/lang/translate';
import { EditorSubSection } from '../pages/draft-page/editor-sub-section';
import { TextInput } from 'tradera/components/form-controls/text-input/text-input';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { showErrorToast } from 'tradera/services/toasts';
import { trackSyiForm } from 'tradera/apps/syi/services/syi-ga4-service';
import { selectDraftId } from 'tradera/apps/syi/state/selectors/draft-selectors';
import { Button } from 'tradera/components/atoms/button/button';
import { ClearTextModal } from 'tradera/apps/syi/components/clear-text-modal/clear-text-modal';
import CloseIcon from 'tradera/blueprint/theme/icons2/close.svg?jsx';

export const MAX_SHORT_DESCRIPTION_LENGTH = 80;

export const ShortDescription = () => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const [toasterShown, setToasterShown] = useState(false);
    const [showClearButton, setShowClearButton] = useState(false);
    const [showClearTextModal, setShowClearTextModal] = useState(false);

    const setShortDescription = (shortDescription: string) =>
        dispatch(shortDescriptionActions.setShortDescription(shortDescription));

    const text = useAppSelector(
        (state) => getSyiSlice(state).shortDescription.shortDescription,
    );
    const validationError = useAppSelector(
        (state) => getSyiSlice(state).shortDescription.validationError,
    );
    const showError = useAppSelector(
        (state) => getSyiSlice(state).syi.allErrorsVisible,
    );
    const draftId = useAppSelector(selectDraftId);

    const characterCount = text ? text.length : 0;

    useEffect(() => {
        if (characterCount > MAX_SHORT_DESCRIPTION_LENGTH && !toasterShown) {
            showErrorToast('syi_shortdescription_error_count_toast');
            setToasterShown(true);
        } else if (
            characterCount <= MAX_SHORT_DESCRIPTION_LENGTH &&
            toasterShown
        ) {
            setToasterShown(false);
        }
    }, [text, toasterShown, t, characterCount]);

    const handleClearTextModalConfirm = () => {
        setShortDescription('');
        setShowClearTextModal(false);
        setShowClearButton(false);
    };

    return (
        <EditorSubSection data-validation-error-anchor="shortDescription">
            <div className="short-description">
                <TextInput
                    name="shortDescription"
                    id="shortDescription"
                    help={`${characterCount}/80`}
                    label={t('syi_shortdescription_title')}
                    value={text ?? undefined}
                    placeholder={t('syi_shortdescription_placeholder')}
                    onChange={(e) => setShortDescription(e.target.value)}
                    error={showError && !!validationError && t(validationError)}
                    collapsed
                    onFocus={() => {
                        setShowClearButton(true);
                        trackSyiForm({
                            eventName: 'syi_title_input',
                            parameters: { draft_id: draftId },
                        });
                    }}
                    onBlur={(e) => {
                        if (e.relatedTarget?.id === 'clear-button') {
                            return;
                        }
                        setShowClearButton(false);
                    }}
                    suffix={
                        showClearButton &&
                        text && (
                            <Button
                                type="button"
                                id="clear-button"
                                // reset paddings:
                                // x-wise it's because therwise the field grows higher than other inputs.
                                // y-wise it's to align it with a suffix icon when it's not a button
                                theme="unbutton"
                                name={t('syi_clear_button_name')}
                                onClick={() => setShowClearTextModal(true)}>
                                <CloseIcon className="icon icon-xs" />
                            </Button>
                        )
                    }
                />
                {showClearTextModal && (
                    <ClearTextModal
                        onConfirm={handleClearTextModalConfirm}
                        onCancel={() => setShowClearTextModal(false)}
                    />
                )}
            </div>
        </EditorSubSection>
    );
};
